import { useMemo } from 'react';
import type { PitchClass, Accidental } from '../types/music';

interface PianoKeyboardProps {
  /** Currently highlighted pitch class */
  highlightedPitchClass: PitchClass | null;
  /** Accidental of the highlighted note */
  highlightedAccidental: Accidental | null;
  /** Type of highlight (correct = green, incorrect = red) */
  highlightType: 'correct' | 'incorrect' | 'none';
  /** Callback when a key is clicked */
  onKeyClick?: (pitchClass: PitchClass, accidental: Accidental) => void;
  /** Whether the keyboard is interactive */
  interactive?: boolean;
  /** Optional className for the container */
  className?: string;
}

// White keys in order
const WHITE_KEYS: PitchClass[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

// Black keys and their positions relative to white keys
// Position is the offset from the left edge of the previous white key
const BLACK_KEYS: { pitchClass: PitchClass; position: number; label: string }[] = [
  { pitchClass: 'C', position: 0, label: 'C#/Db' },
  { pitchClass: 'D', position: 1, label: 'D#/Eb' },
  { pitchClass: 'F', position: 3, label: 'F#/Gb' },
  { pitchClass: 'G', position: 4, label: 'G#/Ab' },
  { pitchClass: 'A', position: 5, label: 'A#/Bb' },
];

/**
 * One-octave piano keyboard component
 *
 * Displays 7 white keys (C to B) and 5 black keys.
 * Keys can be highlighted green (correct) or red (incorrect).
 */
export function PianoKeyboard({
  highlightedPitchClass,
  highlightedAccidental,
  highlightType,
  onKeyClick,
  interactive = false,
  className = ''
}: PianoKeyboardProps) {
  // Determine which key should be highlighted
  const highlightedKey = useMemo(() => {
    if (!highlightedPitchClass || highlightType === 'none') {
      return null;
    }

    // For sharps/flats, we highlight the black key
    // The black key is named after the sharp (e.g., C# is the black key above C)
    if (highlightedAccidental === 'sharp') {
      return { pitchClass: highlightedPitchClass, isBlack: true };
    }

    if (highlightedAccidental === 'flat') {
      // Flat notes map to the black key below (e.g., Db = C#)
      // We need to find the previous pitch class
      const pitchIndex = WHITE_KEYS.indexOf(highlightedPitchClass);
      const prevPitch = WHITE_KEYS[(pitchIndex - 1 + 7) % 7];
      // Check if there's a black key between this pitch and the previous one
      const hasBlackKey = BLACK_KEYS.some(bk => bk.pitchClass === prevPitch);
      if (hasBlackKey) {
        return { pitchClass: prevPitch, isBlack: true };
      }
      // E flat = D#, B flat = A#
      // But Fb = E (white) and Cb = B (white) - these are enharmonic to naturals
      return { pitchClass: highlightedPitchClass, isBlack: false };
    }

    // Natural - highlight white key
    return { pitchClass: highlightedPitchClass, isBlack: false };
  }, [highlightedPitchClass, highlightedAccidental, highlightType]);

  const getHighlightClass = (pitchClass: PitchClass, isBlack: boolean): string => {
    if (!highlightedKey) return '';
    if (highlightedKey.pitchClass !== pitchClass) return '';
    if (highlightedKey.isBlack !== isBlack) return '';

    if (highlightType === 'correct') {
      return isBlack
        ? 'bg-green-500 shadow-lg shadow-green-500/50'
        : 'bg-green-400 shadow-lg shadow-green-400/50';
    }
    if (highlightType === 'incorrect') {
      return isBlack
        ? 'bg-red-500 shadow-lg shadow-red-500/50'
        : 'bg-red-400 shadow-lg shadow-red-400/50';
    }
    return '';
  };

  const handleWhiteKeyClick = (pitchClass: PitchClass) => {
    if (interactive && onKeyClick) {
      onKeyClick(pitchClass, 'natural');
    }
  };

  const handleBlackKeyClick = (pitchClass: PitchClass) => {
    if (interactive && onKeyClick) {
      // Black keys are sharps
      onKeyClick(pitchClass, 'sharp');
    }
  };

  return (
    <div
      className={`piano-keyboard relative select-none ${className}`}
      role="group"
      aria-label="One-octave piano keyboard"
    >
      {/* White keys */}
      <div className="flex">
        {WHITE_KEYS.map((pitch, index) => {
          const highlight = getHighlightClass(pitch, false);
          const isHighlighted = highlight !== '';

          return (
            <button
              key={pitch}
              onClick={() => handleWhiteKeyClick(pitch)}
              disabled={!interactive}
              className={`
                relative
                w-12 h-36
                ${isHighlighted ? highlight : 'bg-white hover:bg-gray-100'}
                border border-gray-300
                ${index === 0 ? 'rounded-l-md' : ''}
                ${index === WHITE_KEYS.length - 1 ? 'rounded-r-md' : ''}
                transition-all duration-150
                flex items-end justify-center pb-2
                ${interactive ? 'cursor-pointer active:bg-gray-200' : 'cursor-default'}
                ${isHighlighted ? 'animate-pulse-correct' : ''}
              `}
              aria-label={`${pitch} natural`}
              aria-pressed={isHighlighted}
            >
              <span className={`
                text-xs font-medium
                ${isHighlighted ? 'text-white font-bold' : 'text-gray-500'}
              `}>
                {pitch}
              </span>
            </button>
          );
        })}
      </div>

      {/* Black keys */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        {BLACK_KEYS.map(({ pitchClass, position, label }) => {
          const highlight = getHighlightClass(pitchClass, true);
          const isHighlighted = highlight !== '';

          // Calculate position: each white key is 48px (w-12)
          // Black key should be positioned between white keys
          const leftOffset = (position + 1) * 48 - 12; // 24px width, centered on boundary

          return (
            <button
              key={`${pitchClass}#`}
              onClick={() => handleBlackKeyClick(pitchClass)}
              disabled={!interactive}
              className={`
                absolute top-0
                w-8 h-24
                ${isHighlighted ? highlight : 'bg-gray-900 hover:bg-gray-800'}
                rounded-b-md
                transition-all duration-150
                pointer-events-auto
                ${interactive ? 'cursor-pointer active:bg-gray-700' : 'cursor-default'}
                ${isHighlighted ? 'animate-pulse-correct' : ''}
                z-10
              `}
              style={{ left: `${leftOffset}px` }}
              aria-label={label}
              aria-pressed={isHighlighted}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact version of the piano keyboard for smaller screens
 */
export function PianoKeyboardCompact({
  highlightedPitchClass,
  highlightedAccidental,
  highlightType,
  className = ''
}: Omit<PianoKeyboardProps, 'onKeyClick' | 'interactive'>) {
  return (
    <PianoKeyboard
      highlightedPitchClass={highlightedPitchClass}
      highlightedAccidental={highlightedAccidental}
      highlightType={highlightType}
      interactive={false}
      className={`transform scale-75 origin-center ${className}`}
    />
  );
}
